import ApiClient from '../composables/api_client';
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { gtm } from '../composables/gtm';
import { useGlobalStore } from '../stores/useGlobalStore';

const globalStore = useGlobalStore();

export const useLuckyboxStore = defineStore('campaign-luckybox', () => {

    const asset = ref('');
    const campaign = ref({});
    const cart = ref({});
    const bundles = ref({});
    const expanded = ref({});

    const states = ref({
        is_loading: false,
        is_finished: false,
    });

    const getAsset = (path, withVer = true) => {
        const base = asset.value.match(/^\/[^?]+/)?.[0] ?? '';
        const ver = asset.value.match(/cv=([^\&]+)/)?.[1] ?? '';
        const fn = path.replace(/^\//, '');
        if (ver) {
            return `${base}/${fn}${withVer ? `?cv=${ver}` : ''}`;
        } else {
            return path;
        }
    };

    const getCombo = (box, pos) => {
        if (!box?.combo) {
            return;
        }
        return box.combo?.[pos] ?? '';
    };

    // 取得已選 items.qty 小計
    const getBundleCount = (bid) => {
        if (!cart.value?.[bid]?.items) {
            return 0;
        }
        const bundle = cart.value[bid];
        return Object.values(bundle.items).reduce((acc, val) => acc + val);
    };

    // 取得剩餘可選數量
    const getBundleRemains = (bid) => {
        if (!cart.value?.[bid]?.items) {
            return 0;
        }
        const bundle = cart.value?.[bid];
        const sum = Object.values(bundle.items).reduce((acc, val) => acc + val, 0);
        return bundle.base - sum;
    };

    // 根據 type 取得 bundles
    const getBundleByType = (type, data = {}) => {
        const boxes = Object.values(data);
        if (boxes.length === 0) {
            return;
        }
        const filtered = boxes.filter(box => box.type === type);
        const rows = filtered.sort((a, b) => a.id.toString().localeCompare(b.id));
        const items = rows.reduce((acc, box) => {
            acc[box.id] = box;
            return acc;
        }, {});
        return items;
    };

    //
    const getAPIEndpoint = (name, params = {}) => {
        try {
            return route(`hc.api.campaigns.${name}`, {
                ...params,
                campaignId: campaign.value.campaign_id,
            });
        } catch (error) {
            throw new Error(`API ${name} not found`, { cause: error });
        }
    };

    // 驗證所選 bundle 是否可以加入購物車
    const isCartReadyForItem = (bid) => {
        if (!cart.value?.[bid]?.items) {
            return false;
        }
        const bundle = cart.value[bid];
        const items = Object.values(bundle.items).reduce((acc, val) => acc + val);
        return bundle.base === items;
    };

    // bundle for campaign_v2
    /**
     * 新增組合產品到購物車
     * @param {string} bid 組合ID
     * @param {boolean=true} isCustom 是否可以自訂數量，預設為 true
     * @returns {Promise<void>}
     */
    const addBundleToCart = async (bid, isCustom = true) => {

        if (!cart.value?.[bid]) {
            console.error('Bundle not found:', bid);
            return;
        }

        const bundle = cart.value[bid];

        if (!bundle?.items) {
            throw new Error('Bundle items not found');
        }

        const params = {
            bundle: bid,
            items: {},
        };

        Object.keys(bundle.items).forEach(item => {
            if (isCustom) {
                params.items[item] = Number(bundle.items[item]);
            } else {
                params.items[item] = Number(bundle.base);
            }
        });

        states.value.is_loading = true;

        try {
            const api = getAPIEndpoint('add-to-cart');
            await ApiClient.post(api, params);
            await globalStore.fetchCart();
        } catch (error) {
            console.error('error', error);
        } finally {
            states.value.is_loading = false;
        }
    };

    // 變更 bundle.item 值
    const setQty = (act, itemid, bid, item) => {

        if (!cart.value?.[bid]) {
            return;
        }

        if (true !== item?.is_available) {
            return;
        }

        const bundle = cart.value[bid];
        const items = cart.value[bid]?.items;
        const sum = Object.values(items).reduce((acc, val) => acc + val, 0);
        let qty = Number.parseInt(items?.[itemid], 10);

        if (isNaN(qty)) {
            qty = 0;
        }

        switch (act) {
            case 'plus':
                if (sum < bundle.base) {
                    qty++;
                }
                break;
            case 'minus':
                if (qty > 0) {
                    qty--;
                }
                break;
        }

        qty = qty < 0 ? 0 : qty;
        cart.value[bid].items[itemid] = qty;
    };

    //GTM
    const setGTM = (item = {}) => {
        if (!gtm) {
            return;
        }

        // for 代理商
        const layer_agent = {
            event: 'addToCart',
            ecommerce: {
                currency: gtm.CURRENCY,
                value: Number.parseFloat(item?.price),
                items: [{
                    item_id: item?.options?.sku ?? item.id,
                    item_name: item?.name,
                    price: Number.parseFloat(item?.price),
                    quantity: 1,
                }]
            }
        };
        // for HC
        const layer_hc = {
            event: 'add_to_cart',
            ecommerce: {
                currency: gtm.CURRENCY,
                value: Number.parseFloat(item?.price),
                items: [{
                    item_id: item?.options?.sku ?? item.id,
                    item_name: item?.name,
                    price: Number.parseFloat(item?.price),
                    quantity: 1,
                }]
            }
        };

        // GTM push
        dataLayer.push({ ecommerce: null });
        dataLayer.push(layer_agent);
        dataLayer.push(layer_hc);
        //console.info('[GTM]', 'addToCart', 'debug', layer_agent);
        //console.info('[GTM]', 'add_to_cart', 'debug', layer_hc);
    };

    return {
        asset, campaign, cart, bundles, expanded, states,
        getAsset, getCombo,
        getBundleCount, getBundleRemains, isCartReadyForItem,
        getAPIEndpoint, addBundleToCart, setQty, setGTM, getBundleByType,
    };

});
